import type { AreaMappingRow, BuildingArea, Floor, FloorplanArea, UnmappedZoneRow } from './types';

interface RawBmsZoneEntry {
  id: string;
  buildingId: string;
  floorId?: string;
  name?: string;
  bmsZones?: Array<string | { S?: string; N?: string }>;
  createdAt?: string;
  updatedAt?: string;
}

export const normalizeBmsZones = (value: RawBmsZoneEntry['bmsZones']): string[] => {
  if (!Array.isArray(value)) return [];

  return value
    .map((entry) => {
      if (typeof entry === 'string') return entry;
      if (entry && typeof entry === 'object') {
        if (typeof entry.S === 'string') return entry.S;
        if (typeof entry.N === 'string') return entry.N;
      }
      return '';
    })
    .map((zone) => zone.trim())
    .filter(Boolean);
};

export const normalizeAreas = (items: RawBmsZoneEntry[]): BuildingArea[] =>
  items.map((item) => ({
    id: item.id,
    buildingId: item.buildingId,
    floorId: item.floorId ?? '',
    name: item.name ?? item.id,
    bmsZones: normalizeBmsZones(item.bmsZones),
    createdAt: item.createdAt,
    updatedAt: item.updatedAt,
  }));

export const buildAreaRowKey = (floorId: string, areaId: string) => `${floorId}:${areaId}`;

export const compareZoneIds = (a: string, b: string) => {
  const numA = Number(a);
  const numB = Number(b);
  if (!Number.isNaN(numA) && !Number.isNaN(numB)) return numA - numB;
  return a.localeCompare(b);
};

/** Use API inventory when present; otherwise infer a sensible default list. */
export const resolveZoneInventory = (zoneInventory: string[], dbAreas: BuildingArea[]): string[] => {
  const fromApi = zoneInventory.map((zoneId) => zoneId.trim()).filter(Boolean);
  if (fromApi.length > 0) {
    return [...new Set(fromApi)].sort(compareZoneIds);
  }

  const assigned = dbAreas.flatMap((area) => area.bmsZones);
  const numeric = assigned.map(Number).filter((n) => !Number.isNaN(n));
  const max = numeric.length > 0 ? Math.max(...numeric, 5) : 5;
  return Array.from({ length: max + 1 }, (_, index) => String(index)).sort(compareZoneIds);
};

export const getAvailableZonesForArea = (
  row: AreaMappingRow,
  zoneInventory: string[],
  dbAreas: BuildingArea[],
): string[] => {
  const inventory = resolveZoneInventory(zoneInventory, dbAreas);
  const assignedGlobally = new Set(dbAreas.flatMap((area) => area.bmsZones));
  return inventory.filter((zoneId) => !assignedGlobally.has(zoneId) && !row.bmsZones.includes(zoneId));
};

export const buildAreaRows = (
  floors: Floor[],
  floorplanAreasByFloor: Record<string, FloorplanArea[]>,
  dbAreas: BuildingArea[],
): AreaMappingRow[] => {
  const floorNameById = new Map(floors.map((floor) => [floor.id, floor.name]));
  const rows: AreaMappingRow[] = [];

  for (const floor of floors) {
    const floorAreas = floorplanAreasByFloor[floor.id] ?? [];
    const floorName = floorNameById.get(floor.id) ?? floor.id;

    for (const floorArea of floorAreas) {
      const dbRecord =
        dbAreas.find((area) => area.id === floorArea.key && area.floorId === floor.id) ?? null;
      const bmsZones = dbRecord?.bmsZones ?? [];

      rows.push({
        rowKey: buildAreaRowKey(floor.id, floorArea.key),
        areaId: floorArea.key,
        name: dbRecord?.name ?? floorArea.name,
        floorId: floor.id,
        floorName,
        floorplanFloors: [floorName],
        bmsZones,
        dbRecord,
        isUnmappedArea: !dbRecord || bmsZones.length === 0,
        updatedAt: dbRecord?.updatedAt,
      });
    }
  }

  return rows.sort((a, b) => {
    if (a.floorId === b.floorId) {
      return a.areaId.localeCompare(b.areaId);
    }
    return a.floorId.localeCompare(b.floorId);
  });
};

export const buildUnmappedZoneRows = (zoneInventory: string[], dbAreas: BuildingArea[]): UnmappedZoneRow[] => {
  const inventory = resolveZoneInventory(zoneInventory, dbAreas);
  const assigned = new Set(dbAreas.flatMap((area) => area.bmsZones));
  return inventory.filter((zoneId) => !assigned.has(zoneId)).map((zoneId) => ({ zoneId }));
};

export const countUnmappedAreas = (rows: AreaMappingRow[]) => rows.filter((row) => row.isUnmappedArea).length;

export const countUnmappedZones = (zoneInventory: string[], dbAreas: BuildingArea[]) =>
  buildUnmappedZoneRows(zoneInventory, dbAreas).length;
